import { Controller } from '@hotwired/stimulus';

const DEBOUNCE_DELAY = 500; // 500ms debounce

export default class extends Controller {
  static values = { 
    url: String,
    fieldId: Number 
  }

  connect() {
    this.debounceTimer = null;
    this.isLoading = false;
  }

  disconnect() {
    this.clearTimer();
  }

  save(event) {
    this.clearTimer();

    // Don't auto-save during submission
    if (this.isLoading) return;

    // For file inputs, save immediately without debounce
    if (event.target.type === 'file') {
      const files = event.target.files;
      if (files && files.length > 0) {
        // Show the file input when user is changing file
        event.target.style.display = 'block';
        this.performSave(files[0]); // Save the first file
      }
      return;
    }

    this.debounceTimer = setTimeout(() => {
      let value;

      // Handle checkboxes for multi-select fields
      if (event.target.type === 'checkbox' && event.target.name.includes('[]')) {
        const fieldName = event.target.name;
        const checkedBoxes = document.querySelectorAll(`input[name="${fieldName}"]:checked`);
        value = Array.from(checkedBoxes).map(cb => cb.value);
      } else {
        value = event.target.value;
      }

      this.performSave(value);
    }, DEBOUNCE_DELAY);
  }

  clearTimer() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  async performSave(value) {
    this.isLoading = true;
    this.showSavingState();

    const formData = new FormData();
    formData.append('field_id', this.fieldIdValue);

    // Handle file objects
    if (value instanceof File) {
      formData.append('field_value', value);
    }
    // Handle array values (for multi-select checkboxes)
    else if (Array.isArray(value)) {
      if (value.length === 0) {
        formData.append('field_value', '');
      } else {
        value.forEach(val => formData.append('field_value[]', val));
      }
    } else {
      formData.append('field_value', value || '');
    }

    formData.append('authenticity_token', this.getAuthenticityToken());

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        this.showSavedState();

        // For file uploads, also update UI manually as fallback
        if (value instanceof File) {
          this.updateFileUIAfterUpload(value.name, value);
        }
      } else {
        this.showErrorState();
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      this.showErrorState();
    } finally {
      this.isLoading = false;
      this.hideSavingStateAfterDelay();
    }
  }

  showSavingState() {
    this.removePreviousIndicators();
    this.addSaveIndicator('💾', 'text-muted', 'Saving...');
  }

  showSavedState() {
    this.removePreviousIndicators();
    this.addSaveIndicator('✅', 'text-success', 'Saved');
  }

  showErrorState() {
    this.removePreviousIndicators();
    this.addSaveIndicator('❌', 'text-danger', 'Save failed');
  }

  addSaveIndicator(icon, cssClass, title) {
    const indicator = document.createElement('span');
    indicator.className = `auto-save-indicator ${cssClass} ms-2`;
    indicator.innerHTML = `<small>${icon}</small>`;
    indicator.title = title;
    
    const fieldContainer = this.element.closest('.form-field');
    if (fieldContainer) {
      const label = fieldContainer.querySelector('.field-label');
      if (label) {
        label.appendChild(indicator);
      }
    }
  }

  removePreviousIndicators() {
    const indicators = document.querySelectorAll('.auto-save-indicator');
    indicators.forEach(indicator => indicator.remove());
  }

  hideSavingStateAfterDelay() {
    setTimeout(() => {
      this.removePreviousIndicators();
    }, 2000); // Remove indicator after 2 seconds
  }

  updateFileUIAfterUpload(filename, file) {
    const fieldId = this.fieldIdValue;

    // Hide the file input
    const fileInput = document.getElementById(`field_${fieldId}`);
    if (fileInput) {
      fileInput.style.display = 'none';
    }

    // Determine file type and create appropriate preview
    const isImage = file && file.type.startsWith('image/');
    const { icon, color, label } = this.getFileTypeInfo(filename);

    // Show the current file section with enhanced UI
    const currentFileSection = document.getElementById(`field_${fieldId}_current_file`);
    if (currentFileSection) {
      currentFileSection.style.display = 'block';

      if (isImage) {
        // Create image preview
        const reader = new FileReader();
        reader.onload = (e) => {
          currentFileSection.innerHTML = this.createFilePreviewHTML(fieldId, filename, label, null, e.target.result, icon, color);
        };
        reader.readAsDataURL(file);
      } else {
        // Create file icon preview
        currentFileSection.innerHTML = this.createFilePreviewHTML(fieldId, filename, label, null, null, icon, color);
      }
    }
  }

  getFileTypeInfo(filename) {
    const extension = filename.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return { icon: 'fas fa-file-pdf', color: 'text-danger', label: 'PDF' };
      case 'doc':
      case 'docx':
        return { icon: 'fas fa-file-word', color: 'text-primary', label: 'Word' };
      case 'xls':
      case 'xlsx':
        return { icon: 'fas fa-file-excel', color: 'text-success', label: 'Excel' };
      case 'ppt':
      case 'pptx':
        return { icon: 'fas fa-file-powerpoint', color: 'text-warning', label: 'PowerPoint' };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return { icon: 'fas fa-image', color: 'text-info', label: 'Image' };
      case 'mp4':
      case 'avi':
      case 'mov':
        return { icon: 'fas fa-video', color: 'text-purple', label: 'Video' };
      case 'mp3':
      case 'wav':
      case 'flac':
        return { icon: 'fas fa-music', color: 'text-success', label: 'Audio' };
      case 'zip':
      case 'rar':
      case '7z':
        return { icon: 'fas fa-file-archive', color: 'text-secondary', label: 'Archive' };
      case 'txt':
        return { icon: 'fas fa-file-alt', color: 'text-muted', label: 'Text' };
      case 'csv':
        return { icon: 'fas fa-file-csv', color: 'text-success', label: 'CSV' };
      default:
        return { icon: 'fas fa-file', color: 'text-muted', label: 'File' };
    }
  }

  createFilePreviewHTML(fieldId, filename, label, size, imageSrc, icon, color) {
    const previewContent = imageSrc
      ? `<img src="${imageSrc}" class="img-thumbnail" style="max-width: 80px; max-height: 80px; object-fit: cover;" alt="${filename}">`
      : `<i class="fa-3x ${icon} ${color}"></i>`;

    return `
      <div class="file-preview p-3 border rounded bg-light">
        <div class="row align-items-center">
          <div class="col-auto">
            <div class="d-flex align-items-center justify-content-center" style="width: 80px; height: 80px; background: #f8f9fa; border-radius: 8px;">
              ${previewContent}
            </div>
          </div>
          <div class="col">
            <div class="file-details">
              <h6 class="mb-1 text-truncate" title="${filename}">${filename}</h6>
              <small class="text-muted d-block">
                <span class="badge text-bg-secondary me-2">${label}</span>
                ${size ? size : 'Uploaded'}
              </small>
            </div>
          </div>
          <div class="col-auto">
            <div class="file-actions">
              <button class="btn btn-outline-primary btn-sm me-1" type="button" onclick="document.getElementById('field_${fieldId}').click(); document.getElementById('field_${fieldId}').style.display='block';" title="Change file">
                <i class="fas fa-edit me-1"></i>
                Change
              </button>
              <button class="btn btn-outline-danger btn-sm" type="button" title="Delete file">
                <i class="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  getAuthenticityToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement ? tokenElement.content : '';
  }

}